import type {
  ClientConversation,
  ClientConversationMessage,
  ClientUnreadByProgram,
  ClientUnreadConversationsResponse,
  ConversationContextType,
} from '@hellodarwin/app-features/src/features/conversations/types';
import {
  ApiError,
  ApiErrorInitialState,
} from '@hellodarwin/core/lib/features/entities';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../../app/index';
import ClientApiV2 from '../client-api-v2';

export interface ClientConversationContext {
  type: ConversationContextType;
  id: string;
}

export interface ClientConversationThreadState {
  status: 'idle' | 'pending';
  error: ApiError;
  messages: ClientConversationMessage[];
  page: number;
  snapshotAt?: string;
  hasMore: boolean;
}

export interface ClientConversationsState {
  byKey: Record<string, ClientConversationThreadState | undefined>;
  unread: {
    status: 'idle' | 'pending';
    error: ApiError;
    total: number;
    byProgram: Record<string, number>;
  };
}

const initialState: ClientConversationsState = {
  byKey: {},
  unread: {
    status: 'idle',
    error: ApiErrorInitialState,
    total: 0,
    byProgram: {},
  },
};

const contextKey = (ctx: ClientConversationContext) => `${ctx.type}:${ctx.id}`;

const ensureThread = (
  state: ClientConversationsState,
  key: string,
): ClientConversationThreadState => {
  const existing = state.byKey[key];
  if (existing) return existing;
  const next: ClientConversationThreadState = {
    status: 'idle',
    error: ApiErrorInitialState,
    messages: [],
    page: 1,
    snapshotAt: undefined,
    hasMore: false,
  };
  state.byKey[key] = next;
  return next;
};

const clearDeletedReplyParentPreviews = (
  messages: ClientConversationMessage[],
  deletedMessageId: string,
) => {
  messages.forEach((message) => {
    if (message.reply_parent_message_id === deletedMessageId) {
      message.reply_parent_message = null;
    }
  });
};

export const fetchConversation = createAsyncThunk<
  {
    context: ClientConversationContext;
    messages: ClientConversationMessage[];
    page: number;
    snapshotAt?: string;
    limit?: number;
  },
  {
    api: ClientApiV2;
    context: ClientConversationContext;
    page?: number;
    snapshotAt?: string;
    limit?: number;
  },
  { rejectValue: ApiError }
>(
  'client/fetchConversation',
  async (
    { api, context, page = 1, snapshotAt, limit },
    { rejectWithValue },
  ) => {
    try {
      const conv = await api
        .createAxios()
        .get<ClientConversation>(
          `/client-conversations/${context.type}/${context.id}`,
          {
            params: {
              page,
              snapshot_at: snapshotAt,
              limit,
            },
          },
        );
      const data = conv?.data;
      const messages: ClientConversationMessage[] = data?.messages ?? [];
      return {
        context,
        messages,
        page: page,
        snapshotAt: data?.snapshot_at ?? snapshotAt,
        limit,
      };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? ApiErrorInitialState);
    }
  },
);

export const sendConversationMessage = createAsyncThunk<
  { context: ClientConversationContext; message: ClientConversationMessage },
  {
    api: ClientApiV2;
    context: ClientConversationContext;
    content: string;
    replyParentMessageId?: string;
  },
  { rejectValue: ApiError }
>(
  'client/sendConversationMessage',
  async ({ api, context, content, replyParentMessageId }, { rejectWithValue }) => {
    try {
      const { data: message } = await api
        .createAxios()
        .post<ClientConversationMessage>(
          `/client-conversations/${context.type}/${context.id}/messages`,
          {
            content,
            reply_parent_message_id: replyParentMessageId,
          },
        );
      return { context, message };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? ApiErrorInitialState);
    }
  },
);

export const deleteConversationMessage = createAsyncThunk<
  { context: ClientConversationContext; messageId: string },
  { api: ClientApiV2; context: ClientConversationContext; messageId: string },
  { rejectValue: ApiError }
>(
  'client/deleteConversationMessage',
  async ({ api, context, messageId }, { rejectWithValue }) => {
    try {
      await api
        .createAxios()
        .delete(`/client-conversations/messages/${messageId}`);
      return { context, messageId };
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? ApiErrorInitialState);
    }
  },
);

export const fetchUnreadConversationCounts = createAsyncThunk<
  ClientUnreadConversationsResponse,
  { api: ClientApiV2; contextType: ConversationContextType },
  { rejectValue: ApiError }
>(
  'client/fetchUnreadConversationCounts',
  async ({ api, contextType }, { rejectWithValue }) => {
    try {
      const { data } = await api
        .createAxios()
        .get<ClientUnreadConversationsResponse>(
          '/client-conversations/unread',
          {
            params: { contextType: contextType },
          },
        );
      return data;
    } catch (err: any) {
      return rejectWithValue(err?.response?.data ?? ApiErrorInitialState);
    }
  },
);

const conversationsSlice = createSlice({
  name: 'clientContextConversations',
  initialState,
  reducers: {
    resetConversation: (
      state,
      action: PayloadAction<{ context: ClientConversationContext }>,
    ) => {
      state.byKey[contextKey(action.payload.context)] = {
        status: 'idle',
        error: ApiErrorInitialState,
        messages: [],
        page: 1,
        snapshotAt: undefined,
        hasMore: true,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchConversation.pending, (state, { meta }) => {
      const s = ensureThread(state, contextKey(meta.arg.context));
      s.status = 'pending';
    });
    builder.addCase(fetchConversation.fulfilled, (state, { payload }) => {
      const s = ensureThread(state, contextKey(payload.context));
      if ((payload.page ?? 1) <= 1) {
        s.messages = payload.messages ?? [];
      } else {
        s.messages = [...(s.messages ?? []), ...(payload.messages ?? [])];
      }
      s.hasMore = (payload.messages?.length ?? 0) === (payload.limit ?? 20);
      s.page = payload.page ?? 1;
      s.snapshotAt = payload.snapshotAt;
      s.status = 'idle';
    });
    builder.addCase(fetchConversation.rejected, (state, { meta, payload }) => {
      const s = ensureThread(state, contextKey(meta.arg.context));
      s.error = payload ?? ApiErrorInitialState;
      s.status = 'idle';
    });

    builder.addCase(sendConversationMessage.pending, (state, { meta }) => {
      const s = ensureThread(state, contextKey(meta.arg.context));
      s.status = 'pending';
    });
    builder.addCase(sendConversationMessage.fulfilled, (state, { payload }) => {
      const s = ensureThread(state, contextKey(payload.context));
      s.messages = [payload.message, ...(s.messages ?? [])];
      s.status = 'idle';
    });
    builder.addCase(
      sendConversationMessage.rejected,
      (state, { meta, payload }) => {
        const s = ensureThread(state, contextKey(meta.arg.context));
        s.error = payload ?? ApiErrorInitialState;
        s.status = 'idle';
      },
    );

    builder.addCase(
      deleteConversationMessage.fulfilled,
      (state, { payload }) => {
        const s = ensureThread(state, contextKey(payload.context));
        s.messages = (s.messages ?? []).filter(
          (m) => m.message_id !== payload.messageId,
        );
        clearDeletedReplyParentPreviews(s.messages, payload.messageId);
      },
    );
    builder.addCase(
      deleteConversationMessage.rejected,
      (state, { meta, payload }) => {
        const s = ensureThread(state, contextKey(meta.arg.context));
        s.error = payload ?? ApiErrorInitialState;
      },
    );

    builder.addCase(fetchUnreadConversationCounts.pending, (state) => {
      state.unread.status = 'pending';
    });
    builder.addCase(
      fetchUnreadConversationCounts.fulfilled,
      (state, { payload }) => {
        state.unread.status = 'idle';
        state.unread.total = payload?.total ?? 0;
        const map: Record<string, number> = {};
        (payload?.programs ?? []).forEach((p: ClientUnreadByProgram) => {
          if (p.program_id) map[p.program_id] = p.count ?? 0;
        });
        state.unread.byProgram = map;
      },
    );
    builder.addCase(
      fetchUnreadConversationCounts.rejected,
      (state, { payload }) => {
        state.unread.status = 'idle';
        state.unread.error = payload ?? ApiErrorInitialState;
      },
    );
  },
});

export const { resetConversation } = conversationsSlice.actions;

export const selectConversationMessagesByContext = (
  state: RootState,
  context: ClientConversationContext,
) => state.contextConversations.byKey[contextKey(context)]?.messages ?? [];

export const selectConversationLoadingByContext = (
  state: RootState,
  context: ClientConversationContext,
) =>
  (state.contextConversations.byKey[contextKey(context)]?.status ?? 'idle') ===
  'pending';

export const selectConversationHasMoreByContext = (
  state: RootState,
  context: ClientConversationContext,
) => state.contextConversations.byKey[contextKey(context)]?.hasMore ?? false;

export const selectConversationPageByContext = (
  state: RootState,
  context: ClientConversationContext,
) => state.contextConversations.byKey[contextKey(context)]?.page ?? 0;

export const selectConversationSnapshotAtByContext = (
  state: RootState,
  context: ClientConversationContext,
) => state.contextConversations.byKey[contextKey(context)]?.snapshotAt;

export const contextConversationsReducer = conversationsSlice.reducer;

export const selectUnreadConversationTotal = (state: RootState) =>
  state.contextConversations.unread.total ?? 0;

export const selectUnreadConversationByProgram = (state: RootState) =>
  state.contextConversations.unread.byProgram ?? {};

export const selectUnreadCountByProgramId = (state: RootState) => {
  const byProgram = state.contextConversations.unread.byProgram ?? {};
  return (programId: string) => byProgram[programId] ?? 0;
};
